import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { getFormFieldError } from '@api-ui-app/src/app/lib/getFormFieldError';
import { QuickQuestionAnimation } from '@api-ui-app/src/app/on-boarding/constants/quick-question-animations';
import { ErrorData } from '@api-ui-app/src/app/shared/lib/error-data';
import { PhoneNumberService } from '@gelato-api-ui/core/quick-questions/services/phone-number.service';
import { QuickQuestionsService } from '@gelato-api-ui/core/quick-questions/services/quick-questions.service';
import { QuickQuestionOptionsCollection } from '@gelato-api-ui/core/quick-questions/types/quick-question-options-collection';
import {
  QuickQuestions,
  QuickQuestionsSteps,
  quickQuestionConfig,
} from '@gelato-api-ui/core/quick-questions/types/quick-questions';
import { ResponsiveService } from '@gelato-api-ui/core/responsive/responsive.service';
import { CountryWithDialingCode } from '@gelato-api-ui/sdk/src/lib/shared/country-selector/types/country-selector';
import { CountryCode } from 'libphonenumber-js';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'gd-quick-question',
  templateUrl: './quick-question.component.html',
  styleUrls: ['./quick-question.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [QuickQuestionAnimation],
})
export class QuickQuestionComponent implements OnInit, OnDestroy {
  @Input() quickQuestions: QuickQuestions = new QuickQuestions();
  @Input() activeStep: QuickQuestionsSteps;
  @Input() countries: CountryWithDialingCode[];
  @Input() error: ErrorData;

  @Output() quickQuestionsChange = new EventEmitter<QuickQuestions>();

  phoneNumber: string = null;
  countryCode$ = new BehaviorSubject<CountryCode>(null);
  ngOnDestroy$ = new Subject();

  quickQuestionsSteps = QuickQuestionsSteps;
  quickQuestionConfig = quickQuestionConfig;
  numberCollapsedElementsForMobile = 4;
  numberCollapsedElementsForDesktop = 2;
  quickQuestionsOptionsCollection: QuickQuestionOptionsCollection;

  constructor(
    private readonly quickQuestionsService: QuickQuestionsService,
    private readonly phoneNumberService: PhoneNumberService,
    private readonly responsiveService: ResponsiveService,
  ) {}

  ngOnInit() {
    this.responsiveService.onScreenSizeChanges();
    if (this.quickQuestions.phoneNumber) {
      this.phoneNumber = this.quickQuestions.phoneNumber;
    }

    if (this.quickQuestions.phonePrefix) {
      const countryCode = this.phoneNumberService.getCountryCodeByPhonePrefix(
        this.countries,
        this.quickQuestions.phonePrefix,
      );

      this.countryCode$.next(countryCode);
    }

    this.quickQuestionsOptionsCollection = this.randomizeOptionsOrder(this.quickQuestionsService.getOptions());
  }

  get activeStepConfig() {
    return this.quickQuestionConfig[this.activeStep];
  }

  get numberOfCollapsedElements$(): Observable<number> {
    return this.responsiveService.isMobile$.pipe(
      map(isMobile => (isMobile ? this.numberCollapsedElementsForMobile : this.numberCollapsedElementsForDesktop)),
    );
  }

  hasError(fieldName: string): boolean {
    return Boolean(getFormFieldError(this.error, fieldName));
  }

  /**
   * Save value and clean other fields by demand
   * @param stepName
   * @param value
   */
  // FIXME: remove any and fix type
  setAnswer(stepName: QuickQuestionsSteps, value: any) {
    this.quickQuestions = this.quickQuestionsService.setAnswer(this.quickQuestions, this.countries, stepName, value);
    this.phoneNumber = this.phoneNumberService.getFormattedPhoneNumberByPhonePrefix(
      this.countries,
      this.quickQuestions.phonePrefix,
      this.quickQuestions.phoneNumber,
      true,
    );
    this.quickQuestionsChange.emit(this.quickQuestions);
  }

  ngOnDestroy(): void {
    this.ngOnDestroy$.next(true);
    this.ngOnDestroy$.complete();
    this.responsiveService.onRemoveScreenSizeChanges();
  }

  private randomizeOptionsOrder(optionsCollection: QuickQuestionOptionsCollection): QuickQuestionOptionsCollection {
    const NO_RANDOMIZE_OPTION_KEYS = [
      QuickQuestionsSteps.revenue,
      QuickQuestionsSteps.ecommerceStoreYearlyRevenue,
      QuickQuestionsSteps.ecommercePlatform,
      QuickQuestionsSteps.followers,
    ];
    const randomizedCollection = {};

    for (const key in optionsCollection) {
      if (optionsCollection.hasOwnProperty(key)) {
        const options = optionsCollection[key];
        randomizedCollection[key] = NO_RANDOMIZE_OPTION_KEYS.includes(key as QuickQuestionsSteps)
          ? options
          : [...options].sort(() => Math.random() - 0.5);
      }
    }

    return randomizedCollection;
  }
}
